import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Condition } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Badge } from "@mantine/core";

import { isEmpty } from "lodash";

import { useProblemsSubscription } from "@/clients/utils/hooks";
import {
  fetchProblemsBadges,
  filterProblemsBadgesByVisibility,
  filterProblemsByType,
} from "@/clients/zod-schemas/problems";
import { ProblemType } from "@/fhir/enums/quilted-health";
import { RootState, useAppDispatch } from "@/store";
import { loadProblems, selectSortedProblemsList } from "@/store/slices/client-problems";
import { joinReferenceString } from "@/utils/medplum";

import { ClientBadgesWrapper } from "./ClientBadgesWrapper";
import classes from "./ClientHealthSummary.module.css";
import { ClientHealthSummaryTypeEnum } from "./enums";

interface ClientProblemsProps {
  title: string;
  type: ProblemType;
  noReportedProblems?: boolean;
  onClick?: () => void;
}

export default function ClientProblems({
  title,
  type,
  noReportedProblems,
  onClick,
}: ClientProblemsProps) {
  const { clientId } = useParams() as { clientId: string };
  const dispatch = useAppDispatch();
  const allProblems = useSelector<RootState, Condition[]>(selectSortedProblemsList);
  const matchedProblems = filterProblemsByType(allProblems, type);
  const visibleProblems = filterProblemsBadgesByVisibility(matchedProblems, type);
  const medplum = useMedplum();

  const subscriptionCriteria = `Condition?_sort=-_lastUpdated&subject=${joinReferenceString("Patient", clientId)}`;

  useEffect(() => {
    const fetchProblems = async () => {
      const problems = await fetchProblemsBadges(medplum, clientId);
      dispatch(loadProblems(problems as Condition[]));
    };

    fetchProblems();
  }, [medplum, dispatch, clientId]);

  useProblemsSubscription({ subscriptionCriteria, dispatch, extensionValue: "update" });
  useProblemsSubscription({ subscriptionCriteria, dispatch, extensionValue: "create" });

  return (
    <ClientBadgesWrapper
      title={title}
      onClick={onClick}
      noInfo={isEmpty(visibleProblems)}
      summaryType={ClientHealthSummaryTypeEnum.Problem}
      noneReported={noReportedProblems && isEmpty(visibleProblems)}
    >
      {visibleProblems.map((display) => {
        return (
          <Badge
            px="md"
            py="sm"
            c="black"
            tt="none"
            bg="white"
            key={display}
            variant="filled"
            className={classes.patientBadge}
          >
            {display}
          </Badge>
        );
      })}
    </ClientBadgesWrapper>
  );
}
